// What people have done in the laundrette, as things a visitor can see there (Sam, 3 Nov: "the actions to visitors,
// becoming real things that they see happen in the world - so if for example there is an action to leave a red sock
// somewhere - then this actually happens"). One entry for every action in lib/world/placeActions.js's laundry pool
// (test/laundretteThings.test.js keeps them in step), read by components/three/world/LaundretteThings.jsx, which is
// told by the town which were done in the last day (sim-server Traces.js thingsAt).
//
// Everything is in the pack's own units and axes (Blender's x, y and z up, before Sam's scale of 1.5): x runs out to
// the street, y along the building. Read off the blend (collection Laundrette): the hall is x -1.95..4.84 and y
// -2.28..7.02; the six washers along the back wall stand at y 6.14..7.02 with their doors at y 6.1 and tops at z 1.28,
// numbered from the street, so machine one is nearest the window; the island of ten is x 0.34..2.11, y 0.08..4.32;
// the stacked dryers are set in the wall at x -1.95; the folding table is x -1.92..-0.31, y -2.29..-1.39, top 1.08;
// the benches under the big window are x 4.12..4.75 at y 0.42..2.19 and 3.22..4.99, seats at 0.62.
//
// A part is one of
//   { box: [w, d, h], at: [x, y, z], c }      a block w across x, d across y, h tall, standing on z (yaw, rx: degrees)
//   { cyl: [r, h], at, c }                    a round thing standing up (axis: 'y' or 'x' to lie it along one)
//   { ball: r, at, c }                        centred on at
//   { decal: name, size: [w, h], at, face }   a flat picture (DECALS) facing '+x', '-x', '+y', '-y' or 'up'; spin
//   { drum: machine, c, mins }                a drum going round behind that machine's door for so many minutes
// and `for` on a thing cuts how long it shows (minutes), when the deed is over sooner than the day the town keeps it.

// the six along the back wall, from the street end; a door's middle is (x, 6.1, 0.625)
export const MACHINES = { one: 4.355, two: 3.49, three: 2.645, four: 1.785, five: 0.94, six: 0.075 }
// the island's ten: five face the dryers (x 0.29), five the window (x 2.15), at these y
export const ISLAND_Y = [0.47, 1.31, 2.17, 3.01, 3.88]
// the dryer door in the wall nearest the machines; its drum is drawn just inside the glass (x -1.89..-1.83), as the
// dryer's own back is only a hand behind it
export const DRYER = { x: -1.885, y: 5.575, z: 1.775, r: 0.34 }

// The few things the pack has no model of and the actions need, always there: a radiator, a plastic chair by the
// door, a corkboard over machines five and six, a whiteboard by the door, a change machine on the back wall, a shelf
// of lost property over the folding table, a dial on every washer, and a puddle by machine one. Each is in the state
// the actions find it in; an action that changes one names it in `sets` below.
export const FIXTURES = {
  radiator: { at: [1.22, -2.23, 0.15] },
  // its back to the street wall, facing into the hall; turned, it is pulled out and faces the machines
  chair: { at: [4.42, -1.88], yaw: 0, turned: { at: [4.15, -1.62], yaw: -60 } },
  corkboard: { at: [0.9, 6.995, 1.6], size: [1.2, 0.8] },
  whiteboard: { at: [4.82, -1.88, 1.55], size: [0.66, 0.52] },
  change: { at: [-1.45, 6.86, 0.9] },
  shelf: { at: [-1.1, -2.15, 1.65], w: 1.2 },
  puddle: { at: [4.3, 5.7, 0.006], size: [0.7, 0.45] },
}
// where a dial sits on each washer's panel, beside the middle, and which way it faces
export const DIALS = [
  ...Object.values(MACHINES).map((x) => ({ at: [x + 0.26, 6.125, 1.12], face: '-y' })),
  ...ISLAND_Y.map((y) => ({ at: [0.325, y - 0.26, 1.12], face: '-x' })),
  ...ISLAND_Y.map((y) => ({ at: [2.125, y + 0.26, 1.12], face: '+x' })),
]

// The pictures, one 128 px cell each in a 4 by 4 sheet drawn once on the page.
export const DECALS = [
  'smiley', 'stillSpinning', 'rainNote', 'poem', 'sockDrawing', 'handprints', 'footprints', 'puddle',
  'star', 'lostSock', 'shortWash', 'washMe', 'one', 'two', 'three', 'rosette',
]

const back = (m, dx = 0) => MACHINES[m] + dx
const onTop = (m, dx, dy, z = 1.28) => [back(m, dx), 6.58 + dy, z]
// a laundry basket like the pack's: dark wicker with a lighter rim, and the washing in it when there is some
const basket = (x, y, z, { yaw = 0, top = null, w = 0.6, h = 0.55 } = {}) => [
  { box: [w, w, h - 0.04], at: [x, y, z], c: '#3b2f27', yaw },
  { box: [w + 0.03, w + 0.03, 0.04], at: [x, y, z + h - 0.04], c: '#5a4636', yaw },
  ...(top ? [{ box: [w - 0.08, w - 0.08, 0.05], at: [x, y, z + h - 0.03], c: top, yaw }] : []),
]

export const LAUNDRETTE_THINGS = {
  'fold-the-abandoned-washing-into-a-neat-pile': [
    { box: [0.34, 0.3, 0.05], at: [-1.55, -1.84, 1.08], c: '#e9e5dc', yaw: 4 },
    { box: [0.33, 0.29, 0.05], at: [-1.55, -1.84, 1.13], c: '#93acc8', yaw: -2 },
    { box: [0.32, 0.28, 0.05], at: [-1.55, -1.84, 1.18], c: '#cfc6b7', yaw: 3 },
  ],
  'put-a-single-red-sock-in-with-the-whites': [
    { still: 'three', c: '#e7a7b9' },
    { box: [0.07, 0.22, 0.03], at: onTop('three', -0.15, -0.1), c: '#c3282e', yaw: 25 },
  ],
  'watch-a-full-spin-cycle-without-looking-away': [{ drum: 'five', c: '#7f9fc4', mins: 45 }],
  'leave-exact-change-on-top-of-washing-machine-fou': [0, 1, 2, 3].map((i) => (
    { cyl: [0.035, 0.012], at: onTop('four', -0.2, -0.15, 1.28 + i * 0.012), c: i % 2 ? '#b98a4e' : '#c9ccd1' })),
  'draw-a-smiley-in-the-steam-on-the-window': [{ decal: 'smiley', size: [0.5, 0.5], at: [4.885, 1.45, 1.75], face: '-x' }],
  'set-three-washing-machines-going-at-once-empty': [
    { drum: 'one', c: '#8a8e93', mins: 45 }, { drum: 'two', c: '#8a8e93', mins: 45 }, { drum: 'six', c: '#8a8e93', mins: 45 },
  ],
  'leave-a-paperback-on-the-folding-table': [{ box: [0.13, 0.2, 0.03], at: [-0.72, -1.62, 1.08], c: '#c4572e', yaw: -18 }],
  'warm-your-hands-on-the-dryer-door': [{ decal: 'handprints', size: [0.58, 0.42], at: [-1.82, DRYER.y, DRYER.z], face: '+x' }],
  'pair-every-odd-sock-in-the-lost-sock-basket': ['#d9d2c3', '#3d5a80', '#c3282e'].flatMap((c, i) => [
    { box: [0.06, 0.2, 0.025], at: [1.45 + i * 0.13, 4.62, 0.6], c },
    { box: [0.06, 0.2, 0.025], at: [1.51 + i * 0.13, 4.64, 0.625], c },
  ]),
  'leave-one-glove-on-the-radiator-fingers-up': [
    { box: [0.1, 0.035, 0.1], at: [1.22, -2.2, 0.72], c: '#5b3f8c' },
    ...[0, 1, 2, 3].map((i) => ({ box: [0.018, 0.03, 0.075], at: [1.183 + i * 0.025, -2.2, 0.82], c: '#5b3f8c' })),
  ],
  'write-still-spinning-in-the-dust-on-washing-mach': [{ decal: 'stillSpinning', size: [0.6, 0.3], at: onTop('two', 0, -0.05, 1.286), face: 'up' }],
  // the chair turned to face the machines, pulled out from the wall (FIXTURES.chair.turned)
  'sit-in-the-plastic-chair-and-just-wait': [{ sets: 'chair' }],
  'tape-a-coin-under-the-folding-table': [
    { cyl: [0.035, 0.008], at: [-1.2, -1.86, 1.02], c: '#c9ccd1' },
    { box: [0.12, 0.03, 0.004], at: [-1.2, -1.86, 1.016], c: '#e8e2c8' },
  ],
  'leave-the-good-detergent-where-anyone-can-use-it': [
    { box: [0.12, 0.08, 0.26], at: onTop('one', -0.1, -0.1), c: '#e07a2c' },
    { box: [0.121, 0.081, 0.006], at: onTop('one', -0.1, -0.1, 1.42), c: '#2b2b2b' },
    { cyl: [0.025, 0.04], at: onTop('one', -0.1, -0.1, 1.54), c: '#f2efe8' },
  ],
  // every dial turned the same way, to the gentlest (DIALS)
  'turn-every-dial-to-the-gentlest-setting': [{ sets: 'dials' }],
  'leave-a-note-that-says-your-washing-smells-like-': [{ decal: 'rainNote', size: [0.26, 0.26], at: [back('two'), 7.0, 1.62], face: '-y', spin: -5 }],
  'stack-the-baskets-into-a-tower': [0, 1, 2, 3, 4].flatMap((i) => basket(3.3, 5.55, i * 0.56, { yaw: [0, 6, -4, 9, -7][i] })),
  // the puddle goes (FIXTURES.puddle), and the mop is left leaning in the corner
  'mop-the-puddle-by-washing-machine-one': [
    { sets: 'puddle' },
    { cyl: [0.02, 1.25], at: [4.74, 5.98, 0.08], c: '#a07a4c', rx: 12 },
    { box: [0.28, 0.1, 0.1], at: [4.7, 5.72, 0], c: '#d8d3c4' },
  ],
  'hum-along-with-the-drum-until-they-sync': [{ drum: 'four', c: '#a8c48f', mins: 30 }],
  'leave-a-pound-in-the-change-washing-machine-tray': [{ cyl: [0.03, 0.01], at: [-1.45, 6.7, 1.005], c: '#d4af37' }],
  'hide-behind-the-dryers-and-jump-out-at-nobody': [2.5, 3.1, 3.7].map((y, i) => (
    { decal: 'footprints', size: [0.5, 0.6], at: [-3.45 + (i % 2) * 0.06, y, 0.006], face: 'up', spin: i === 2 ? 25 : 0 })),
  // the notices on the corkboard set straight (FIXTURES.corkboard)
  'read-the-notices-on-the-corkboard-all-of-them': [{ sets: 'notices' }],
  'pin-a-drawing-of-a-sock-to-the-corkboard': [{ decal: 'sockDrawing', size: [0.24, 0.24], at: [1.3, 6.965, 2.12], face: '-y', spin: 4 }],
  'balance-a-marble-on-the-rim-of-the-drum': [{ ball: 0.025, at: [back('six'), 6.07, 0.955], c: '#3f8fd8' }],
  'clean-the-filter-nobody-has-cleaned-in-years': [
    { box: [0.32, 0.2, 0.06], at: [-1.7, 4.95, 0], c: '#9b968d', yaw: 8 },
    { box: [0.28, 0.17, 0.05], at: [-1.7, 4.95, 0.06], c: '#a8a399', yaw: -5 },
  ],
  // the front door stays open (Laundrette.jsx), with a wedge under it
  'leave-the-door-propped-so-the-warmth-gets-out': [
    { sets: 'door' },
    { box: [0.12, 0.06, 0.05], at: [5.5, -0.5, 0], c: '#6b4a2b' },
  ],
  'write-a-wash-day-poem-on-the-whiteboard': [{ decal: 'poem', size: [0.58, 0.44], at: [4.795, -1.88, 1.81], face: '-x' }],
  'run-a-coat-through-on-hot-until-it-shrinks': [
    { box: [0.15, 0.12, 0.025], at: [-1.15, -1.6, 1.08], c: '#7a4b3a', yaw: 10 },
    { box: [0.05, 0.1, 0.02], at: [-1.24, -1.62, 1.08], c: '#7a4b3a', yaw: 40 },
    { box: [0.05, 0.1, 0.02], at: [-1.06, -1.58, 1.08], c: '#7a4b3a', yaw: -25 },
  ],
  // the lost property on the shelf sorted by colour (FIXTURES.shelf)
  'sort-the-lost-property-by-colour': [{ sets: 'shelf' }],
  'stick-a-gold-star-on-every-washing-machine': [
    ...Object.values(MACHINES).map((x) => ({ decal: 'star', size: [0.13, 0.13], at: [x - 0.24, 6.125, 1.14], face: '-y' })),
    ...ISLAND_Y.map((y) => ({ decal: 'star', size: [0.13, 0.13], at: [0.325, y + 0.24, 1.14], face: '-x' })),
    ...ISLAND_Y.map((y) => ({ decal: 'star', size: [0.13, 0.13], at: [2.125, y - 0.24, 1.14], face: '+x' })),
  ],
  'leave-a-folded-paper-crane-on-machine-three': [
    { box: [0.1, 0.04, 0.05], at: onTop('three', 0.18, -0.15), c: '#f4f1ea', yaw: 30 },
    { box: [0.02, 0.16, 0.012], at: onTop('three', 0.18, -0.15, 1.32), c: '#f4f1ea', yaw: 30, rx: 18 },
    { box: [0.015, 0.015, 0.06], at: onTop('three', 0.23, -0.12, 1.32), c: '#f4f1ea' },
  ],
  'time-the-spin-cycle-against-your-heartbeat': [{ drum: 'two', c: '#c48fa8', mins: 20 }],
  'leave-a-bag-of-mint-imperials-on-the-shelf': [
    { box: [0.12, 0.08, 0.11], at: [-0.6, -2.15, 1.69], c: '#f3f2ee', yaw: -8 },
    { ball: 0.02, at: [-0.49, -2.1, 1.71], c: '#fbfbf8' },
    { ball: 0.02, at: [-0.45, -2.16, 1.71], c: '#fbfbf8' },
  ],
  'write-lost-one-sock-sentimental-on-the-board': [{ decal: 'lostSock', size: [0.26, 0.19], at: [0.5, 6.965, 1.85], face: '-y', spin: -3 }],
  // they left with the gloves; the dryer they warmed them in is still going round
  'dry-your-gloves-and-wear-them-warm-out-the-door': [{ drum: 'dryer', c: '#b5a27a', mins: 20 }],
  'leave-20p-taped-to-a-note-that-says-short-wash': [{ decal: 'shortWash', size: [0.2, 0.16], at: [back('four'), 6.125, 1.06], face: '-y' }],
  'arrange-the-powder-cups-into-a-little-skyline': [0.04, 0.09, 0.06, 0.12, 0.05, 0.1, 0.07].map((h, i) => (
    { cyl: [0.028, h], at: onTop('five', -0.27 + i * 0.075, -0.2), c: i % 2 ? '#dfe7ee' : '#b9cfe0' })),
  'sit-two-strangers-washing-side-by-side-matchmaki': [
    ...basket(4.43, 0.95, 0.62, { top: '#d77a8f', w: 0.42, h: 0.32 }),
    ...basket(4.43, 1.39, 0.62, { top: '#5f7fa8', w: 0.42, h: 0.32, yaw: 5 }),
  ],
  'leave-a-wash-me-note-on-the-cleanest-machine': [{ decal: 'washMe', size: [0.2, 0.14], at: [back('six'), 6.125, 1.06], face: '-y', spin: 3 }],
  // a card on every seat, and the rosette on the plastic chair, which wobbles
  'test-every-chair-and-rank-them': [
    { decal: 'one', size: [0.12, 0.12], at: [0, 0, 0.465], face: 'up', on: 'chair' },
    { decal: 'rosette', size: [0.16, 0.2], at: [0.175, 0, 0.72], face: '-x', on: 'chair' },
    { decal: 'two', size: [0.12, 0.12], at: [4.43, 3.6, 0.63], face: 'up', spin: 90 },
    { decal: 'three', size: [0.12, 0.12], at: [4.43, 1.75, 0.63], face: 'up', spin: 90 },
  ],
}

/** Which actions change a fixture, by the fixture's name. */
export const SETS = Object.fromEntries(Object.entries(LAUNDRETTE_THINGS)
  .flatMap(([id, parts]) => parts.filter((p) => p.sets).map((p) => [p.sets, id])))

/** How long a thing shows after the deed, in ms: the shortest `mins` or `for` of its parts, else the town's day. */
export function showsFor(id, day = 24 * 3600 * 1000) {
  const mins = (LAUNDRETTE_THINGS[id] || []).map((p) => p.mins || p.for).filter(Boolean)
  return mins.length ? Math.min(...mins) * 60_000 : day
}
